import React from 'react'
import { Button, Input, Label, Card, CardContent } from '@/shared/ui'
import { supabase } from '@/lib/supabase'
import SaveFeedbackModal from '@/shared/ui/SaveFeedbackModal'
import { useToast } from '@/shared/ui/Toast'
import { getVercelConfig, setVercelConfig } from '../services/settingsService'

export default function VercelConfigForm() {
  const { add: toast } = useToast()
  const [projectId, setProjectId] = React.useState('')
  const [token, setToken] = React.useState('')
  const [hasToken, setHasToken] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const [applying, setApplying] = React.useState(false)
  const [applyMsg, setApplyMsg] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const cfg = await getVercelConfig()
        if (cancelled) return
        setProjectId(cfg.project_id ?? '')
        setHasToken(!!cfg.has_token)
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Falha ao carregar integração Vercel')
      } finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [])

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaving(true)
      await setVercelConfig(projectId.trim(), token.trim())
      setHasToken(!!token.trim())
      setToken('')
      toast({ title: 'Vercel', description: 'Configuração salva com sucesso.', variant:'success', soft:true })
      setSaved(true)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao salvar configuração da Vercel')
    } finally {
      setSaving(false)
    }
  }

  async function onApply(e: React.FormEvent) {
    e.preventDefault()
    setApplyMsg(null)
    try {
      setApplying(true)
      const env: Record<string,string|undefined> = {
        VITE_SUPABASE_URL: (import.meta as any)?.env?.VITE_SUPABASE_URL,
        VITE_SUPABASE_ANON_KEY: (import.meta as any)?.env?.VITE_SUPABASE_ANON_KEY,
        VITE_STRIPE_PUBLISHABLE_KEY: (import.meta as any)?.env?.VITE_STRIPE_PUBLISHABLE_KEY,
        VITE_MASTER_EMAIL: (import.meta as any)?.env?.VITE_MASTER_EMAIL,
        VITE_AVATARS_BUCKET: (import.meta as any)?.env?.VITE_AVATARS_BUCKET,
      }
      const { data, error } = await supabase.functions.invoke('apply-vercel-config', { body: { env, targets: ['production','preview'] } })
      if (error) throw error
      setApplyMsg(`Config aplicada. Envs atualizadas: ${(data?.updated?.envs||[]).join(', ')}`)
    } catch (e:any) {
      setApplyMsg(e?.message || 'Falha ao aplicar no Vercel')
    } finally {
      setApplying(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <form className="space-y-4 pt-4" onSubmit={onSave} aria-busy={saving || loading}>
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
          <div className="space-y-1.5">
            <Label>Project ID</Label>
            <Input value={projectId} onChange={(e)=> setProjectId(e.target.value)} placeholder="prj_..." />
          </div>
          <div className="space-y-1.5">
            <Label>Token {hasToken && <span className="text-xs text-foreground/60">(já configurado)</span>}</Label>
            <Input value={token} onChange={(e)=> setToken(e.target.value)} type="password" placeholder="VERCEL_TOKEN" />
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saving || loading}>{saving ? 'Salvando...' : 'Salvar'}</Button>
            <Button type="button" variant="outline" onClick={onApply} disabled={applying || loading || !projectId || !hasToken}>{applying ? 'Aplicando...' : 'Aplicar no Vercel'}</Button>
          </div>
          {applyMsg && <div className="text-xs text-foreground/70">{applyMsg}</div>}
        </form>
      </CardContent>
      <SaveFeedbackModal open={saved} onClose={()=> setSaved(false)} title="Vercel" description="Configuração salva com sucesso." />
    </Card>
  )
}

